import React from 'react';
import { Control, Controller, FieldErrors, FieldValues, Path } from 'react-hook-form';
import './GeneralSelect.css';
import { useTranslation } from 'react-i18next';

interface Props<TFieldValues extends FieldValues = FieldValues> {
  label: string;
  name: Path<TFieldValues> | string;
  translationTag?: string;
  preSelectedVal?: string;
  value: string;
  control: Control<TFieldValues>;
  required?: boolean;
  data: any;
  setSelectedVal?: (val: any) => void;
  errors: FieldErrors<TFieldValues>;
}

const GeneralSelect = <TFieldValues extends FieldValues = FieldValues>(props: Props<TFieldValues>) => {
  const { label, name, translationTag, control, required, data, value, preSelectedVal, setSelectedVal, errors } = props;
  const { i18n } = useTranslation();
  const isError = !!errors[name as keyof typeof errors];
  const safeData = Array.isArray(data) ? data : [];

  const getOptionLabel = (item: any) => {
    const rawValue = item?.[value] ?? '';
    if (!translationTag) {
      return rawValue;
    }
    const translationKey = `${translationTag}.${rawValue}`;
    return i18n.t(translationKey, { defaultValue: rawValue });
  };

  return (
    <div className="selectContainer">
      <label
        htmlFor={name}
        style={{
          color: isError ? 'red' : undefined,
        }}
      >
        {label}
      </label>
      <Controller
        name={name as Path<TFieldValues>}
        control={control}
        rules={{ required }}
        render={({ field }) => (
          <select
            className={`form-select ${isError ? 'error-border' : ''}`}
            {...field}
            value={field.value ?? ''} // Default null to empty string
            required={required}
            onChange={(e) => {
              setSelectedVal?.(e.target.value);
              field.onChange(e);
            }}
          >
            <option value="">{preSelectedVal || 'Select an option'}</option>
            {safeData.map((item: any, index: number) => (
                <option key={index} value={item.id}>
                  {getOptionLabel(item)}
                </option>
              ))}
          </select>
        )}
      />

      {isError && <p className="error-text">This field is required.</p>}
    </div>
  );
};

export default GeneralSelect;
